import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  /** @type {number | undefined} */
  #showFrame;
  /** @type {Promise<unknown> | null} */
  #openingBarrier = null;
  /** @type {Promise<void> | null} */
  #closingPromise = null;

  connectedCallback() {
    super.connectedCallback();
    // Escape may target the page after a cart row containing focus is removed.
    // Native cancel does not bubble; capture it so every close restores scroll.
    this.addEventListener('cancel', this.#handleCancel, true);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    this.#openingBarrier = null;
    super.disconnectedCallback();
    if (this.#showFrame !== undefined) cancelAnimationFrame(this.#showFrame);
    this.#showFrame = undefined;
    this.#handleResize.cancel();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('cancel', this.#handleCancel, true);
    if (this.refs.dialog.open) {
      this.refs.dialog.close();
      this.#restorePageScroll();
    }
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openingBarrier || this.#showFrame !== undefined) return;

    // A product gallery/size dialog may need to finish closing before the cart opens.
    // Pages without such a dialog follow the original synchronous path.
    const waits = [];
    document.dispatchEvent(new CustomEvent('bouchilino:before-dialog-open', {
      detail: { component: this, waitUntil: (promise) => waits.push(promise) },
    }));
    if (waits.length) {
      const barrier = Promise.all(waits);
      this.#openingBarrier = barrier;
      void barrier.then(() => {
        if (this.#openingBarrier !== barrier) return;
        this.#openingBarrier = null;
        if (this.isConnected) this.showDialog();
      }).catch(() => { if (this.#openingBarrier === barrier) this.#openingBarrier = null; });
      return;
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#showFrame = requestAnimationFrame(() => {
      this.#showFrame = undefined;
      if (!this.isConnected || dialog.open) return;
      // History entries must capture the page position before the fixed-body lock.
      this.dispatchEvent(new DialogBeforeOpenEvent());
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      try {
        dialog.showModal();
      } catch (error) {
        this.#restorePageScroll();
        throw error;
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = () => {
    this.#openingBarrier = null;
    if (this.#showFrame !== undefined) cancelAnimationFrame(this.#showFrame);
    this.#showFrame = undefined;
    if (this.#closingPromise) return this.#closingPromise;
    if (!this.refs.dialog.open) return Promise.resolve();

    this.#closingPromise = this.#finishClosing().finally(() => {
      this.#closingPromise = null;
    });
    return this.#closingPromise;
  };

  async #finishClosing() {
    const { dialog } = this.refs;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#restorePageScroll();

    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.isConnected) this.dispatchEvent(new DialogCloseEvent());
  }

  #restorePageScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    // A cart section morph can close the dialog before removing this listener.
    // Its next trigger click must not cancel the frame that is opening it again.
    if (!dialog.open) return;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /** @param {Event} event */
  #handleCancel = (event) => {
    if (event.target !== this.refs.dialog) return;
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogBeforeOpenEvent extends CustomEvent {
  constructor() {
    super(DialogBeforeOpenEvent.eventName);
  }

  static eventName = 'dialog:before-open';
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
